import { MIR_ACCEPTED_STATES, MIR_DATA_HELD_STATES } from '@nbx/core/booking';

import type { Booking } from '@core/interfaces/booking.interface';
import type { BookingMirWorkflow } from '@services/booking-mir-workflow.service';
import { isOtaBooking } from '@utils/booking-channel.util';

import { BookingGeneralState, BookingMirState } from './booking-states.config';
import {
  type BookingAction,
  BookingStatus,
  bookingStatusView,
  isCancelledBookingStatus,
  normalizeBookingStatus,
} from './booking-status.config';

export interface BookingGuestAccess {
  /** The booking's guest data travels the channel "booking legal" flow (Idempotency-Key + legal version). */
  readonly legal: boolean;
  readonly edit: boolean;
  readonly send: boolean;
  /** Hospedajes already holds this data: a new send is only meaningful after the guests changed. */
  readonly resendRequiresChange: boolean;
}

const LOCKED: BookingGuestAccess = {
  legal: false,
  edit: false,
  send: false,
  resendRequiresChange: false,
};

const LOCAL_ALREADY_SENT = new Set(['BOOKING_NOTIFICATION_PENDING', 'CHECKIN_PENDING']);
const CANONICAL_ACTIVE_GENERAL = new Set<string>([
  BookingGeneralState.CHECKIN_SCHEDULED,
  BookingGeneralState.CHECKOUT_PENDING,
  BookingGeneralState.CHECKOUT_HOST_CONFIRMATION_PENDING,
  BookingGeneralState.BOOKING_CHECKOUT_ERROR,
]);
const CANONICAL_EDITABLE_MIR = new Set<string>([
  BookingMirState.MIR_START,
  BookingMirState.GUEST_MIR_DATA_PENDING,
  BookingMirState.MIR_DATA_READY_PAYMENT_PENDING,
  BookingMirState.SEND_GUEST_MIR_DATA_PENDING,
  BookingMirState.BOOKING_NOTIFICATION_PENDING,
  BookingMirState.BOOKING_NOTIFICATION_ERROR,
  BookingMirState.CHECKIN_PENDING,
  BookingMirState.CHECKIN_NOTIFICATION_PENDING,
  BookingMirState.CHECKIN_ERROR,
  BookingMirState.MIR_COMPLETED,
]);
const CANONICAL_SEND_BLOCKED_MIR = new Set<string>([
  BookingMirState.MIR_DATA_READY_PAYMENT_PENDING,
]);

/**
 * True when the backend exposes a legal read model for the booking, which it does only for
 * bookings owned by a sales channel. Their guest writes are governed by `capabilities`, not by the
 * status matrix, and every write must quote the legal version it was read at.
 */
export function isLegalManagedBooking(booking: Booking | null | undefined): boolean {
  return !!booking && booking.legalStatus != null && booking.legalStatus !== '';
}

/**
 * What the host may do with a booking's guests. A local booking follows the shared status matrix
 * (MANAGE_GUESTS = backend guestUpdateAllowed, SEND_GUESTS = mirDataSendAllowed); a channel
 * booking follows the capabilities the backend computed from its legal status, which fail closed.
 */
export function bookingGuestAccess(booking: Booking | null | undefined): BookingGuestAccess {
  if (!booking) {
    return LOCKED;
  }
  if (isLegalManagedBooking(booking)) {
    const capabilities = booking.capabilities;
    return {
      legal: true,
      edit: capabilities?.legalGuestUpdateAllowed === true,
      send: capabilities?.legalSendAllowed === true || capabilities?.legalRetryAllowed === true,
      resendRequiresChange: booking.legalStatus === 'CHECKIN_PENDING',
    };
  }
  if (isOtaBooking(booking)) {
    return LOCKED;
  }
  if (booking.mirState && CANONICAL_ACTIVE_GENERAL.has(booking.bookingStatus)) {
    const edit = CANONICAL_EDITABLE_MIR.has(booking.mirState);
    return {
      legal: false,
      edit,
      send: edit && !CANONICAL_SEND_BLOCKED_MIR.has(booking.mirState),
      resendRequiresChange: MIR_DATA_HELD_STATES.has(booking.mirState),
    };
  }
  if (normalizeBookingStatus(booking.bookingStatus) === BookingStatus.CHECKIN_SCHEDULED) {
    const edit = booking.manualMirManaged !== true;
    return { legal: false, edit, send: edit, resendRequiresChange: false };
  }
  const view = bookingStatusView(booking.bookingStatus);

  return {
    legal: false,
    edit: view.actions.includes('MANAGE_GUESTS'),
    send: view.actions.includes('SEND_GUESTS'),
    resendRequiresChange: view.status !== null && LOCAL_ALREADY_SENT.has(view.status),
  };
}

/** Canonical MIR states (`BookingWorkflow.Mir`) after Hospedajes acknowledged the data. */
export { MIR_ACCEPTED_STATES };

/** Canonical MIR states once the guests left for Hospedajes, answered or not. */
export const MIR_SENT_STATES: ReadonlySet<string> = new Set([
  BookingMirState.BOOKING_NOTIFICATION_PENDING,
  BookingMirState.CHECKIN_ERROR,
  ...MIR_ACCEPTED_STATES,
]);

/**
 * Whether the workflow says the guests or the stay changed since Hospedajes last got them; null
 * when the view does not carry the flags, so the caller falls back to the booking timestamps.
 */
export function workflowChangesPending(
  workflow: BookingMirWorkflow | null | undefined
): boolean | null {
  const state = workflow?.state;
  if (typeof state?.changesPending !== 'boolean') {
    return null;
  }
  return state.changesPending || state.mirStayChangesPending === true;
}

/** The workflow a send from this screen was made on: its version and MIR state at the press. */
export interface GuestSendMark {
  readonly version: number | null;
  readonly mirState: string | null;
}

/** Marks a send made on `workflow`, so the screen can tell when a re-read reflects it. */
export function guestSendMark(workflow: BookingMirWorkflow | null | undefined): GuestSendMark {
  return {
    version: typeof workflow?.version === 'number' ? workflow.version : null,
    mirState: workflow?.mirState ?? null,
  };
}

/**
 * Whether the workflow read reflects the send `mark` stands for: its version moved (any transition
 * bumps it) or, without versions, its MIR state did. A repeat send of unchanged guests is a silent
 * no-op in the backend, so its workflow never moves and the send stays marked.
 */
export function workflowReflectsSend(
  mark: GuestSendMark | null,
  workflow: BookingMirWorkflow | null | undefined
): boolean {
  if (!mark) {
    return true;
  }
  if (!workflow) {
    return false;
  }
  if (mark.version !== null && typeof workflow.version === 'number') {
    return workflow.version !== mark.version;
  }
  return (workflow.mirState ?? null) !== mark.mirState;
}

/** What the send-once gate of the guests screen reads. */
export interface GuestResendGate {
  /** Hospedajes holds the data in the current MIR state (`BookingGuestAccess.resendRequiresChange`). */
  readonly resendRequiresChange: boolean;
  /** A channel booking: its legal capabilities decide the send on their own. */
  readonly legal: boolean;
  /** The guests changed on this screen since its last send. */
  readonly changedSinceSend: boolean;
  /** This screen sent the guests in this session. */
  readonly sentHere: boolean;
  /** The workflow read already reflects that send (`workflowReflectsSend`). */
  readonly sendReflected: boolean;
  /** The workflow's pending-changes flags (`workflowChangesPending`); null when not published. */
  readonly workflowChangesPending: boolean | null;
  /** The booking timestamps' answer (`guestsChangedSinceLastSend`), the last fallback. */
  readonly changedSinceLastSend: boolean;
}

/**
 * Whether a new send must wait for a change: a send just made here holds until the workflow
 * reflects it; then the MIR state decides (an error or a correction state lets it through), and in
 * a sent state only a change does, read off the workflow flags, else the booking timestamps.
 */
export function guestResendBlocked(gate: GuestResendGate): boolean {
  if (gate.legal || gate.changedSinceSend) {
    return false;
  }
  if (gate.sentHere && !gate.sendReflected) {
    return true;
  }
  if (!gate.resendRequiresChange) {
    return false;
  }
  if (gate.sentHere) {
    return true;
  }
  return gate.workflowChangesPending === null
    ? !gate.changedSinceLastSend
    : !gate.workflowChangesPending;
}

/** Why the host cannot reactivate a skipped registration right now; null when they can. */
export type MirReactivationBlock = 'OWNER_ONLY' | 'NOT_ALLOWED' | 'CLOSING' | 'CLOSE_FAILED';

const PRE_ACCEPTANCE_GENERAL = new Set<string>([
  BookingGeneralState.START,
  BookingGeneralState.INSTANT_CHOICE,
  BookingGeneralState.ACCEPTANCE_PENDING,
]);

/**
 * Confirmed and not cancelled, as far as the workflow's general state tells; a view without it
 * leaves the call to the backend.
 */
function isConfirmedLive(workflow: BookingMirWorkflow): boolean {
  const general = workflow.generalState;
  return !general || (!PRE_ACCEPTANCE_GENERAL.has(general) && !isCancelledBookingStatus(general));
}

/**
 * Whether the host may skip the registration (POST workflow/manual-mir): the backend takes it only
 * on a confirmed, live booking, which for a booking not skipped yet is what `canEditGuests` says.
 */
export function mirOmitAllowed(workflow: BookingMirWorkflow | null | undefined): boolean {
  if (!workflow || workflow.manualMirManaged) {
    return false;
  }
  return typeof workflow.canEditGuests === 'boolean'
    ? workflow.canEditGuests
    : isConfirmedLive(workflow);
}

/**
 * What keeps a skipped registration from being reactivated (agreement 2505): only the owner may,
 * only on a confirmed, live booking, and only once the manual closure completed without error.
 */
export function mirReactivationBlock(
  workflow: BookingMirWorkflow | null | undefined,
  isOwner: boolean
): MirReactivationBlock | null {
  if (!isOwner) {
    return 'OWNER_ONLY';
  }
  if (!workflow || !isConfirmedLive(workflow)) {
    return 'NOT_ALLOWED';
  }
  if (workflow.mirState === BookingMirState.MIR_CANCELLATION_PENDING) {
    return workflow.state?.lastMirError ? 'CLOSE_FAILED' : 'CLOSING';
  }
  return workflow.state?.lastMirError ? 'CLOSE_FAILED' : null;
}

/**
 * What a local booking's workflow lets the host do with its guests (GET /bookings/{id}/workflow).
 * `canEditGuests` is the backend's own guard on PUT /guests and POST /checkin (MIR active: not
 * skipped, cancelled or closing); `canSendMir` its guard on the send (saved data complete, no
 * reconciliation pending, no pending payment holding it back). Without `canSendMir` the send is
 * held back only by MIR_DATA_READY_PAYMENT_PENDING.
 * Null when the view does not carry the capability, so the caller falls back to the status matrix.
 */
export function workflowGuestAccess(
  workflow: BookingMirWorkflow | null | undefined,
  mirState: string | null | undefined
): BookingGuestAccess | null {
  if (typeof workflow?.canEditGuests !== 'boolean') {
    return null;
  }
  const edit = workflow.canEditGuests;
  const send =
    typeof workflow.canSendMir === 'boolean'
      ? workflow.canSendMir
      : mirState !== BookingMirState.MIR_DATA_READY_PAYMENT_PENDING;
  return {
    legal: false,
    edit,
    send: edit && send,
    resendRequiresChange: MIR_DATA_HELD_STATES.has(mirState ?? ''),
  };
}

/**
 * When the guests last left for Hospedajes, as far as the backend tells: the check-in-day send it
 * stamps in `lastMirDataSentDate`, or the state history's last send. A booking send enters
 * BOOKING_NOTIFICATION_PENDING; a corrected check-in send moves CHECKIN_ERROR straight to
 * CHECKIN_PENDING. Null when the booking has never been sent.
 */
export function lastSendToHospedajes(booking: Booking | null | undefined): number | null {
  if (!booking) {
    return null;
  }
  const history = booking.stateHistory ?? [];
  const candidates = [parseDate(booking.lastMirDataSentDate)];
  history.forEach((entry, index) => {
    const state = normalizeBookingStatus(entry.state);
    const previous = index > 0 ? normalizeBookingStatus(history[index - 1].state) : null;
    if (
      state === BookingStatus.BOOKING_NOTIFICATION_PENDING ||
      (state === BookingStatus.CHECKIN_PENDING && previous === BookingStatus.CHECKIN_ERROR)
    ) {
      candidates.push(parseDate(entry.changedAt));
    }
  });
  const known = candidates.filter((value): value is number => value !== null);

  return known.length ? Math.max(...known) : null;
}

/**
 * Whether the guests were saved after the last send to Hospedajes. A booking never sent counts as
 * changed; one never re-saved since the send does not.
 */
export function guestsChangedSinceLastSend(booking: Booking | null | undefined): boolean {
  const sentAt = lastSendToHospedajes(booking);
  if (sentAt === null) {
    return true;
  }
  const updatedAt = parseDate(booking?.lastGuestsUpdateDate);

  return updatedAt !== null && updatedAt > sentAt;
}

function parseDate(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The actions a booking offers on its settings grid: the status matrix filtered by guest access,
 * plus actions granted by the canonical workflow or a channel's legal capabilities. Outside the
 * legal flow, a booking's guest access comes from its workflow view when loaded (local and channel
 * alike, as on the guests page), since the booking GET carries no MIR state; the guests page stays
 * reachable while MIR is skipped so it can be reactivated.
 */
export function bookingActionsFor(
  booking: Booking | null | undefined,
  workflow?: BookingMirWorkflow | null
): BookingAction[] {
  const local = !!booking && !isLegalManagedBooking(booking);
  const view = bookingStatusView(booking?.bookingStatus);
  const statusAccess = bookingGuestAccess(booking);
  const access = (local ? workflowGuestAccess(workflow, workflow?.mirState) : null) ?? statusAccess;
  const reachable =
    access.edit ||
    (statusAccess.edit && view.actions.includes('MANAGE_GUESTS')) ||
    (local &&
      workflow?.manualMirManaged === true &&
      !isCancelledBookingStatus(booking?.bookingStatus));
  const actions = view.actions.filter(
    action =>
      (action !== 'MANAGE_GUESTS' || reachable) &&
      (action !== 'SEND_GUESTS' || access.send) &&
      (action !== 'CANCEL' || isOtaBooking(booking) || workflow?.canCancelLocally === true)
  );
  if (
    booking &&
    !isOtaBooking(booking) &&
    workflow?.canCancelLocally === true &&
    !actions.includes('CANCEL')
  ) {
    actions.push('CANCEL');
  }
  if (reachable && !actions.includes('MANAGE_GUESTS')) {
    actions.push('MANAGE_GUESTS');
  }
  if (access.send && !actions.includes('SEND_GUESTS')) {
    actions.push('SEND_GUESTS');
  }

  return actions;
}
